<script setup lang="ts">
import { computed, ref } from 'vue';
import { useField, useForm } from 'vee-validate';
import { v4 as uuid } from 'uuid';
import { QuestionPhone, ContactPhone, usePlocState, VisualFeedbackWrapperTypes, VisualFeedbackWrapper, ServicesApiResponse, QuestionYesNo } from '@hfc/ui-core';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionAddress from '@/application/shared/infrastructure/components/questions/QuestionAddress.vue';
import AddressValidationModal from '@/application/shared/infrastructure/components/AddressValidationModal.vue';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import { Address } from '@/application/shared/domain/Address';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { AddressSuggestion, AddressValidationResponse } from '@/application/shared/domain/AddressValidationResponse';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { PHONE_TYPES } from '@hfc/common/src/types/PhoneTypes';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';
import { DentalAndVisionApplicationStore } from '../../store/DentalAndVisionApplicationStore';

const { quoteState } = useQuoteStore();

const props = defineProps<{
  applicationType: AppTypeDentalOrVision;
}>();

const {
  dentalAndVisionApplicationStore,
  checkReQuote,
  primaryApplicant
} = useDentalAndVisionApplication(props.applicationType);

const castedDentalAndVisionApplicationStore = dentalAndVisionApplicationStore as DentalAndVisionApplicationStore;
const dentalAndVisionApplicationState = usePlocState(dentalAndVisionApplicationStore);

interface emitInterface {
  (event: 'setLoading', value: boolean): void;
  (event: 'nextSection'): void;
}

const emits = defineEmits<emitInterface>();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);
const { handleServicesApiError } = useHandleApiError();

const mainAddressRef = ref<InstanceType<typeof QuestionAddress> | null>(null);
const mailingAddressRef = ref<InstanceType<typeof QuestionAddress> | null>(null);
const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const suggestions = ref<Array<AddressSuggestion>>([]);
const showAddressValidationModal = ref(false);
const currentValidationType = ref<ADDRESS_TYPE>(ADDRESS_TYPE.HOME);
const mainPhysicalAddressInvalid = ref(false);
const mailingPhysicalAddressInvalid = ref(false);
const mainAddressFieldKey = ref(uuid());
const mailingAddressFieldKey = ref(uuid());

const forceSelectedMainAddress = computed(() => {
  return dentalAndVisionApplicationState.value.forceSelectedAddresses[props.applicationType]?.[ADDRESS_TYPE.HOME] ?? false;
});

const forceSelectedMailingAddress = computed(() =>
  dentalAndVisionApplicationState.value.forceSelectedAddresses[props.applicationType]?.[ADDRESS_TYPE.MAIL] ?? false
);

const addressToValidate = computed(() =>
  currentValidationType.value === ADDRESS_TYPE.HOME
    ? primaryApplicant.value.main_address
    : primaryApplicant.value.mailing_address
);

const {
  value: sameAsMain,
  errorMessage: sameAsMainError,
} = useField('sameAsMain', 'yes_no_required', { initialValue: primaryApplicant.value.is_mailing_same_as_main });

const {
  value: englishFirst,
  errorMessage: englishFirstError,
} = useField('englishFirst', 'yes_no_required', { initialValue: primaryApplicant.value.is_english_first_language });

const hasAddressChanged = (newAddress: Partial<Address>, previousAddress: Address): boolean => {
  return Object.entries(newAddress).some(([key, newValue]) =>
    !Object.is(newValue, previousAddress[key as keyof Address])
  );
};

const setMainAddressValue = async (value: Partial<Address>): Promise<void> => {
  if (value && hasAddressChanged(value, primaryApplicant.value.main_address as Address)) {
    await dentalAndVisionApplicationStore.setForceSelectedAddress(props.applicationType, ADDRESS_TYPE.HOME, false);
  }

  mainPhysicalAddressInvalid.value = false;
  dentalAndVisionApplicationStore.updateApplicant({ main_address: { ...primaryApplicant.value.main_address, ...value } as Address }, props.applicationType, primaryApplicant.value.main_index);
};

const setPhoneValue = (value: ContactPhone): void => {
  dentalAndVisionApplicationStore.updateApplicant({ phone: value }, props.applicationType, primaryApplicant.value.main_index);
};

const mailingAddressChange = async (value: Partial<Address> | undefined): Promise<void> => {
  if (value && hasAddressChanged(value, (primaryApplicant.value.mailing_address ?? {}) as Address)) {
    await dentalAndVisionApplicationStore.setForceSelectedAddress(props.applicationType, ADDRESS_TYPE.MAIL, false);
  }

  mailingPhysicalAddressInvalid.value = false;
  dentalAndVisionApplicationStore.updateApplicant({ mailing_address: { ...primaryApplicant.value.mailing_address, ...value, type: ADDRESS_TYPE.MAIL } as Address }, props.applicationType, primaryApplicant.value.main_index);
};

const sameAsMainChange = (value: boolean | undefined): void => {
  dentalAndVisionApplicationStore.updateApplicant({ is_mailing_same_as_main: value }, props.applicationType, primaryApplicant.value.main_index);

  if (value === true) {
    dentalAndVisionApplicationStore.updateApplicant({ mailing_address: { type: ADDRESS_TYPE.MAIL } as Address }, props.applicationType, primaryApplicant.value.main_index);
  }
};

const englishFirstChange = (value: boolean | undefined): void => {
  dentalAndVisionApplicationStore.updateApplicant({ is_english_first_language: value }, props.applicationType, primaryApplicant.value.main_index);
};

const handleAddressValidationSuccess = (response: ServicesApiResponse<AddressValidationResponse>, type: ADDRESS_TYPE): boolean => {
  if (!response.data) {
    return true;
  }

  suggestions.value = response.data.suggestions;
  currentValidationType.value = type;

  if (response.data.valid === false) {
    showAddressValidationModal.value = true;

    return false;
  }

  return true;
};

const validatePhysicalAddress = async (address: Address, type: ADDRESS_TYPE): Promise<boolean> => {
  emits('setLoading', true);
  const response = await castedDentalAndVisionApplicationStore.validateAddress(address);
  emits('setLoading', false);

  if (response.error) {
    handleServicesApiError(response.error);

    return false;
  }

  return handleAddressValidationSuccess(response, type);
};

const validateFormFields = async (): Promise<boolean> => {
  const mainAddressValid = await mainAddressRef.value?.handleSubmit();
  const phoneValid = await phoneRef.value?.handleSubmit();
  const localValid = await runValidations();
  const mailAddressValid = sameAsMain.value || await mailingAddressRef.value?.handleSubmit();

  return Boolean(mainAddressValid && mailAddressValid && phoneValid && localValid);
};

const validateMainAddress = async (): Promise<boolean> => {
  const mainAddressValidated = forceSelectedMainAddress.value || await validatePhysicalAddress(
    primaryApplicant.value.main_address as Address,
    ADDRESS_TYPE.HOME
  );

  mainPhysicalAddressInvalid.value = !mainAddressValidated;

  return mainAddressValidated;
};

const validateMailingAddress = async (): Promise<boolean> => {
  if (sameAsMain.value || !primaryApplicant.value.mailing_address) {
    return true;
  }

  const mailingAddressValidated = forceSelectedMailingAddress.value || await validatePhysicalAddress(
    primaryApplicant.value.mailing_address as Address,
    ADDRESS_TYPE.MAIL
  );

  mailingPhysicalAddressInvalid.value = !mailingAddressValidated;

  return mailingAddressValidated;
};

const sectionSubmit = async (): Promise<boolean | undefined> => {
  const isFormValid = await validateFormFields();

  if (!isFormValid) {
    return false;
  }

  const isMainAddressValid = await validateMainAddress();

  if (!isMainAddressValid) {
    return false;
  }

  const shouldReQuote = await checkReQuote(quoteState.value[props.applicationType]);

  if (shouldReQuote) {
    return false;
  }

  const isMailingAddressValid = await validateMailingAddress();

  if (!isMailingAddressValid) {
    return false;
  }

  mainPhysicalAddressInvalid.value = false;
  mailingPhysicalAddressInvalid.value = false;

  return true;
};

const handleHomeAddressConfirmation = async (addressValue: AddressSuggestion | Address): Promise<void> => {
  await setMainAddressValue(addressValue);
  mainPhysicalAddressInvalid.value = false;
  mainAddressFieldKey.value = uuid();
  await dentalAndVisionApplicationStore.setForceSelectedAddress(props.applicationType, ADDRESS_TYPE.HOME, true);

  if (sameAsMain.value || mailingPhysicalAddressInvalid.value === false) {
    emits('nextSection');
  }
};

const handleMailingAddressConfirmation = async (addressValue: AddressSuggestion | Address): Promise<void> => {
  await mailingAddressChange(addressValue);
  mailingPhysicalAddressInvalid.value = false;
  mailingAddressFieldKey.value = uuid();
  await dentalAndVisionApplicationStore.setForceSelectedAddress(props.applicationType, ADDRESS_TYPE.MAIL, true);

  if (mainPhysicalAddressInvalid.value === false) {
    emits('nextSection');
  }
};

const confirmAddress = async (address?: AddressSuggestion): Promise<void> => {
  const addressValue = address || addressToValidate.value;

  if (!addressValue) {
    return;
  }

  if (currentValidationType.value === ADDRESS_TYPE.HOME) {
    await handleHomeAddressConfirmation(addressValue);
  } else if (currentValidationType.value === ADDRESS_TYPE.MAIL) {
    await handleMailingAddressConfirmation(addressValue);
  }

  showAddressValidationModal.value = false;
};

defineExpose({
  sectionSubmit: sectionSubmit
});
</script>

<template>
  <BaseSection title="Address">
    <AddressValidationModal v-if="showAddressValidationModal" :suggestions="suggestions" :strict-address-validation="true" @confirm="confirmAddress" @close="showAddressValidationModal = false" :address-type="currentValidationType"/>
    <VisualFeedbackWrapper :show-box="mainPhysicalAddressInvalid" :severity="VisualFeedbackWrapperTypes.ERROR" title="Please enter a valid US home address">
      <QuestionAddress :key="mainAddressFieldKey" :auto-focus="true" :value="primaryApplicant.main_address" @update:value="setMainAddressValue" ref="mainAddressRef" :id="`${primaryApplicant.main_address?.type}-${primaryApplicant.main_index}`" :application-type="applicationType" :validate-coverage="true" />
    </VisualFeedbackWrapper>
    <QuestionPhone :value="primaryApplicant.phone" :default-phone-type="PHONE_TYPES.MOBILE" @update:value="setPhoneValue" ref="phoneRef" />
    <div>
      <QuestionYesNo question-text="Is your mailing address the same as your home address" id="sameAsMain" @update:modelValue="sameAsMainChange" name="sameAsMain" v-model="sameAsMain" />
      <span v-if="sameAsMainError" class="error">{{ sameAsMainError }}</span>
    </div>
    <VisualFeedbackWrapper v-if="sameAsMain === false" :show-box="mailingPhysicalAddressInvalid" :severity="VisualFeedbackWrapperTypes.ERROR" title="Please enter a valid US mailing address" class="mt-6">
      <QuestionAddress :key="mailingAddressFieldKey" :value="primaryApplicant.mailing_address" @update:value="mailingAddressChange" ref="mailingAddressRef" class="mt-6" :id="`mailing-${primaryApplicant.main_index}`" :application-type="applicationType" address-type="Mailing" :validate-coverage="true" />
    </VisualFeedbackWrapper>
    <div class="mt-3">
      <QuestionYesNo question-text="Is English your preferred spoken and written language?" id="englishFirst" v-model="englishFirst" @update:modelValue="englishFirstChange" name="englishFirst" />
      <span v-if="englishFirstError" class="error">{{ englishFirstError }}</span>
    </div>
  </BaseSection>
</template>
